// Sessões do painel admin. Armazenamos um session-token + metadados do
// user no localStorage. Compat: getToken() continua existindo pra api.ts
// — ele extrai só o token bruto da sessão atual (ou string vazia).
//
// O backend aceita também o ADMIN_TOKEN env como bearer; nesse caso a
// "sessão" guarda { token, user: null } — useMe()/useSession() usam o
// envelope completo, mas fluxos legados ficam intactos.

export type SessionUser = {
  id: number;
  email: string;
  name: string;
  role: "master" | "operator";
  active?: boolean;
  is_env_token?: boolean;
};

export type Session = {
  token: string;
  user: SessionUser | null;
  expires_at: string | null;
  // Fase C SPEC-totp-2fa: marca session enrollment-only (master sem
  // TOTP). UI bloqueia navegação até o user completar setup+enable.
  must_enroll?: boolean;
};

const SESSION_KEY = "adminSession";
const LEGACY_TOKEN_KEY = "adminToken";

export function getSession(): Session | null {
  // 1) novo formato
  const raw = localStorage.getItem(SESSION_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Session;
      if (parsed && typeof parsed.token === "string" && parsed.token.length > 0) {
        return parsed;
      }
    } catch {
      // formato corrompido — descarta silenciosamente.
    }
  }
  // 2) compat: alguém que rodou o build antigo deixou só o token cru.
  const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
  if (legacy) {
    return { token: legacy, user: null, expires_at: null };
  }
  return null;
}

export function setSession(s: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  // Limpa o legado pra evitar drift entre as duas chaves.
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}

export function getToken(): string {
  return getSession()?.token ?? "";
}

// setToken existe pra fluxo "Usar ADMIN_TOKEN" (operador devs colando o
// token-master direto). Cria uma sessão minimalista — useMe() depois
// preenche `user` chamando /auth/me, que pra ADMIN_TOKEN retorna
// {role:"master", is_env_token:true}.
export function setToken(token: string): void {
  setSession({ token, user: null, expires_at: null });
}

export function clearToken(): void {
  clearSession();
}
